// Import react router elements.
import { useLocation, useNavigate } from "react-router-dom";

// Import login page components.
import LoginForm from "./components/LoginForm";

// Import register page components.
import RegisterForm from "./components/RegisterForm";

// Import reset password page components.
import ResetPasswordForm from "./components/ResetPasswordForm";

// Import update password page components.
import UpdatePasswordForm from "./components/UpdatePasswordForm";

// Import update email page components.
import UpdateEmailAddressForm from "./components/UpdateEmailAddressForm";

// Import the reusable authentication features panel.
import { LoginFeatures } from "./components/LoginFeatures";

// Import css style sheet.
import styles from "./LoginPage.module.css";



// Export the login page component.
const Login = () => {

    // Get access to the current URL and location information.
    const urlLocation = useLocation();

    // Get access to programmatic navigation.
    const navigate = useNavigate();



    // Get the current URL pathname.
    const path = urlLocation.pathname;

    // Get the current URL query parameters.
    const params = new URLSearchParams(urlLocation.search);



    // Determine which authentication form should currently be displayed.
    const loginFormType =

        // Check whether the current page is the register page.
        path === "/register"

            // Show the register form.
            ? "register"

            // Check whether the current page is the reset password page.
            : path === "/reset-password"

                // Show the reset password form.
                ? "reset-password"

                // Check whether this is a valid update password URL.
                : path === "/update-password" && params.get("token")

                    // Show the update password form.
                    ? "update-password"

                    // Check whether this is a valid update email URL.
                    : path === "/update_email" && params.get("token")

                        // Show the update email form.
                        ? "update_email"

                        // Default to the standard login form.
                        : "login";



    // Handle switching between authentication forms.
    const handleLoginFormSwitch = (formType) => {

        // Navigate to the registration page when requested.
        if (formType === "register") {

            // Navigate to the registration route.
            navigate("/register");

            // Stop processing the function.
            return;

        }

        // Navigate to the reset password page when requested.
        if (formType === "reset-password") {

            // Navigate to the reset password route.
            navigate("/reset-password");

            // Stop processing the function.
            return;

        }

        // Default back to the standard login page.
        navigate("/login");

    };



    // Return the correct main heading for the current form.
    const getPageTitle = () => {

        // Return the registration heading.
        if (loginFormType === "register") {

            // Return registration title.
            return "Create your account";

        }

        // Return the password reset heading.
        if (loginFormType === "reset-password") {

            // Return reset title.
            return "Reset your password";

        }

        // Return the password update heading.
        if (loginFormType === "update-password") {

            // Return update title.
            return "Choose a new password";

        }

        // Return the email update heading.
        if (loginFormType === "update_email") {

            // Return email update title.
            return "Update your email";

        }

        // Return the normal login heading.
        return "Welcome back";

    };



    // Return the supporting text for the current form.
    const getPageSubtitle = () => {

        // Return registration supporting text.
        if (loginFormType === "register") {

            // Return the registration description.
            return "Create, sell and fulfil products from one place.";

        }

        // Return reset password supporting text.
        if (loginFormType === "reset-password") {

            // Return password reset description.
            return "Enter your details and we'll help you regain access.";

        }

        // Return update password supporting text.
        if (loginFormType === "update-password") {

            // Return password update description.
            return "Choose a secure password for your StrichSuite account.";

        }

        // Return update email supporting text.
        if (loginFormType === "update_email") {

            // Return email update description.
            return "Confirm the new email address for your account.";

        }

        // Return normal login supporting text.
        return "Sign in to manage your stores, products and orders.";

    };



    // Render the authentication page.
    return (

        // Render the complete authentication layout.
        <div className={styles.login_page}>

            {/* Render the form side of the authentication page. */}
            <main className={styles.login_page__form_section}>

                {/* Position the main StrichSuite logo independently from the form.
                <a
                    className={styles.login_page__brand}
                    href="/"
                >

                    
                    <img
                        className={styles.login_page__brand_image}
                        src="/images/strichsuite-logo.png"
                        alt="StrichSuite"
                    />

                </a>
                 */}
                 
                {/* Constrain and centre the actual authentication content. */}
                <div className={styles.login_page__form_inner}>

                    <a href="/">

                        {/* Render the stork-only brand mark. */}
                        <img
                            className={styles.login_page__tagline_logo}
                            src="/images/StrichSuite-logo-tagline.png"
                            alt=""
                            aria-hidden="true"
                        />

                    </a>
                    
                    {/* Render the authentication form introduction. */}
                    <div className={styles.login_page__heading}>
                        
                        <div>

                            {/* Render the title for the currently selected form. */}
                            <h1 className={styles.login_page__title}>
                                {getPageTitle()}
                            </h1>
                        
                            {/* Render supporting text for the currently selected form. */}
                            <p className={styles.login_page__subtitle}>
                                {getPageSubtitle()}
                            </p>

                        </div>

                    </div>

                    {/* Render the currently selected authentication form. */}
                    <div className={styles.login_page__form}>

                        {/* Render the login form when selected. */}
                        {loginFormType === "login" && (

                            // Render the existing login form.
                            <LoginForm
                                onSwitch={handleLoginFormSwitch}
                            />

                        )}

                        {/* Render the registration form when selected. */}
                        {loginFormType === "register" && (

                            // Render the existing registration form.
                            <RegisterForm
                                onSwitch={handleLoginFormSwitch}
                            />

                        )}

                        {/* Render the reset password form when selected. */}
                        {loginFormType === "reset-password" && (

                            // Render the existing password reset form.
                            <ResetPasswordForm
                                onSwitch={handleLoginFormSwitch}
                            />

                        )}

                        {/* Render the update password form when selected. */}
                        {loginFormType === "update-password" && (

                            // Render the existing update password form.
                            <UpdatePasswordForm
                                onSwitch={handleLoginFormSwitch}
                            />

                        )}

                        {/* Render the update email address form when selected. */}
                        {loginFormType === "update_email" && (

                            // Render the existing update email form.
                            <UpdateEmailAddressForm
                                onSwitch={handleLoginFormSwitch}
                            />

                        )}

                    </div>

                    {/* Render the legal information below the form. */}
                    <div className={styles.login_page__legal}>
                       
                        {/* Link to the privacy policy. */}
                        <a
                            className={styles.login_page__legal_link}
                            href="/privacy-policy"
                        >
                            Privacy Policy
                        </a>

                         {/* Render the copyright message. */}
                        <span>
                            © {new Date().getFullYear()} StrichSuite. All Rights Reserved.
                        </span>

                    </div>

                </div>

            </main>

            {/* Render the StrichSuite features panel. */}
            <aside className={styles.login_page__feature_section}>

                {/* Render the reusable features content. */}
                <LoginFeatures />

            </aside>

        </div>

    );

};

// Export the login page.
export default Login;